import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Navigation, Car, X, ChevronUp, ChevronDown } from 'lucide-react';

// Leaflet will be loaded dynamically
let L = null;

// Format name - capitalize first letter of each word
const formatName = (name) => {
  if (!name) return '';
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const RADIUS_OPTIONS = [5, 10, 25, 50, 100];

// Vehicle type colors and SVG icons for map markers
const VEHICLE_MAP_CONFIG = {
  car:     { color: '#3B82F6', icon: '🚗' },  // Blue
  suv:     { color: '#8B5CF6', icon: '🚙' },  // Purple
  bike:    { color: '#F59E0B', icon: '🏍️' },  // Amber
  scooter: { color: '#F97316', icon: '🛵' },  // Orange
  auto:    { color: '#10B981', icon: '🛺' },  // Emerald
  bus:     { color: '#EF4444', icon: '🚌' },  // Red
  'tempo-traveller': { color: '#EC4899', icon: '🚐' }, // Pink
  truck:   { color: '#6366F1', icon: '🚛' },  // Indigo
  van:     { color: '#14B8A6', icon: '🚐' },  // Teal
  pickup:  { color: '#84CC16', icon: '🛻' },  // Lime
  tractor: { color: '#A16207', icon: '🚜' },  // Brown
  jcb:     { color: '#CA8A04', icon: 'img', img: '/images/icons/jcb.png' },  // Yellow-dark
  ambulance: { color: '#DC2626', icon: '🚑' }, // Red-dark
  other:   { color: '#6B7280', icon: '🚘' },  // Gray
};

const getVehicleMapConfig = (vehicleType) => VEHICLE_MAP_CONFIG[vehicleType] || VEHICLE_MAP_CONFIG.car;

const NearbyCarsMap = ({
  cars = [],
  userLocation,
  onCarSelect,
  onViewDetails,
  onBookNow,
  selectedCarId,
  height = '400px',
  className = '',
  centerOnUser = false,
  searchRadius: initialRadius = 10, // in km
  onRadiusChange,
  selectedVehicleType = 'all'
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const userMarkerRef = useRef(null);
  const carMarkersRef = useRef({});
  const routeLineRef = useRef(null);
  const radiusCircleRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCar, setSelectedCar] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [searchRadius, setSearchRadiusLocal] = useState(initialRadius);

  // Sync with parent prop
  useEffect(() => {
    setSearchRadiusLocal(initialRadius);
  }, [initialRadius]);

  const handleRadiusChange = (km) => {
    setSearchRadiusLocal(km);
    if (onRadiusChange) onRadiusChange(km);
  };

  // Load Leaflet dynamically
  useEffect(() => {
    const loadLeaflet = async () => {
      try {
        if (window.L) {
          L = window.L;
          setMapLoaded(true);
          return;
        }

        // Load Leaflet CSS
        if (!document.getElementById('leaflet-css')) {
          const link = document.createElement('link');
          link.id = 'leaflet-css';
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
          link.crossOrigin = '';
          document.head.appendChild(link);
        }

        // Load Leaflet JS
        if (!document.getElementById('leaflet-js')) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.id = 'leaflet-js';
            script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
            script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
            script.crossOrigin = '';
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        L = window.L;
        setMapLoaded(true);
      } catch (err) {
        console.error('Error loading Leaflet:', err);
        setError('Failed to load map library');
      }
    };

    loadLeaflet();
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || mapInstanceRef.current) return;

    // Wait for container to have dimensions
    const initMap = () => {
      try {
        // Ensure Leaflet is loaded
        if (!L && window.L) {
          L = window.L;
        }
        if (!L) {
          // Retry after a short delay if Leaflet not ready
          setTimeout(initMap, 100);
          return;
        }

        const container = mapRef.current;
        if (!container || container.clientWidth === 0 || container.clientHeight === 0) {
          // Retry after a short delay if container has no dimensions
          setTimeout(initMap, 100);
          return;
        }

        // Default center (Bangalore, India)
        const defaultCenter = [12.9716, 77.5946];
        const initialCenter = userLocation?.latitude
          ? [userLocation.latitude, userLocation.longitude]
          : defaultCenter;

        const map = L.map(container, {
          center: initialCenter,
          zoom: 14,
          zoomControl: false
        });

        // Add light-styled map tiles
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          maxZoom: 19
        }).addTo(map);

        // Add zoom control to bottom right
        L.control.zoom({ position: 'bottomright' }).addTo(map);

        mapInstanceRef.current = map;

        // Invalidate size to ensure proper rendering
        setTimeout(() => {
          if (mapInstanceRef.current) {
            mapInstanceRef.current.invalidateSize();
          }
        }, 100);
      } catch (err) {
        console.error('Error initializing map:', err);
        setError('Failed to initialize map');
      }
    };

    initMap();

    return () => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.stop();
          mapInstanceRef.current.off();
          mapInstanceRef.current.remove();
        } catch (e) {}
        mapInstanceRef.current = null;
      }
    };
  }, [mapLoaded]);

  // Create car icon with distance label
  const createCarIcon = (isSelected = false, color = '#3B82F6', distance = null, vehicleType = 'car') => {
    if (!L) return null;

    const config = getVehicleMapConfig(vehicleType);
    const markerColor = isSelected ? '#10B981' : config.color;
    const iconContent = config.icon === 'img'
      ? `<img src="${config.img}" style="width: ${isSelected ? '28px' : '24px'}; height: ${isSelected ? '28px' : '24px'}; object-fit: contain;" />`
      : `<span style="font-size: ${isSelected ? '22px' : '18px'}; line-height: 1;">${config.icon}</span>`;

    const distanceText = distance !== null
      ? (distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance}km`)
      : '';

    return L.divIcon({
      className: 'custom-car-marker',
      html: `
        <div style="
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
        ">
          <div style="
            background: ${markerColor};
            width: ${isSelected ? '48px' : '42px'};
            height: ${isSelected ? '48px' : '42px'};
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 3px solid white;
            box-shadow: 0 4px 12px rgba(0,0,0,0.4);
            animation: ${isSelected ? 'pulse 1.5s infinite' : 'none'};
          ">
            ${iconContent}
          </div>
          ${distanceText ? `
            <div style="
              background: ${isSelected ? '#10B981' : markerColor};
              color: white;
              font-size: 10px;
              font-weight: 600;
              padding: 2px 6px;
              border-radius: 10px;
              margin-top: 2px;
              white-space: nowrap;
              box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            ">${distanceText}</div>
          ` : ''}
        </div>
      `,
      iconSize: [60, 70],
      iconAnchor: [30, 35]
    });
  };

  // Create user location icon - large and visible
  const createUserIcon = () => {
    if (!L) return null;

    return L.divIcon({
      className: 'user-location-marker',
      html: `
        <div style="
          position: relative;
          width: 50px;
          height: 50px;
        ">
          <div style="
            position: absolute;
            width: 50px;
            height: 50px;
            background: rgba(59, 130, 246, 0.25);
            border-radius: 50%;
            animation: pulse 2s infinite;
          "></div>
          <div style="
            position: absolute;
            width: 36px;
            height: 36px;
            top: 7px;
            left: 7px;
            background: rgba(59, 130, 246, 0.4);
            border-radius: 50%;
          "></div>
          <div style="
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 20px;
            height: 20px;
            background: #3B82F6;
            border: 4px solid white;
            border-radius: 50%;
            box-shadow: 0 3px 10px rgba(0,0,0,0.4);
          "></div>
        </div>
        <style>
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.3); opacity: 0.5; }
            100% { transform: scale(1); opacity: 1; }
          }
        </style>
      `,
      iconSize: [50, 50],
      iconAnchor: [25, 25]
    });
  };

  // Update user location marker - with retry mechanism
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 5;
    let retryTimer = null;

    const addUserMarker = () => {
      // Check all requirements
      if (!mapInstanceRef.current || !userLocation?.latitude) {
        if (retryCount < maxRetries) {
          retryCount++;
          retryTimer = setTimeout(addUserMarker, 500);
        }
        return;
      }

      // Ensure Leaflet is available
      if (!L && window.L) {
        L = window.L;
      }
      if (!L) {
        if (retryCount < maxRetries) {
          retryCount++;
          retryTimer = setTimeout(addUserMarker, 500);
        }
        return;
      }

      const position = [userLocation.latitude, userLocation.longitude];

      // Remove old marker if exists
      if (userMarkerRef.current) {
        try {
          mapInstanceRef.current.removeLayer(userMarkerRef.current);
        } catch (e) {}
        userMarkerRef.current = null;
      }

      // Create user icon
      const userIcon = L.divIcon({
        className: 'user-location-marker',
        html: `
          <div style="
            position: relative;
            width: 40px;
            height: 40px;
          ">
            <div style="
              position: absolute;
              width: 40px;
              height: 40px;
              background: rgba(59, 130, 246, 0.3);
              border-radius: 50%;
              animation: userPulse 2s infinite;
            "></div>
            <div style="
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 18px;
              height: 18px;
              background: #3B82F6;
              border: 4px solid white;
              border-radius: 50%;
              box-shadow: 0 2px 8px rgba(0,0,0,0.4);
            "></div>
          </div>
        `,
        iconSize: [40, 40],
        iconAnchor: [20, 20]
      });

      // Add user marker
      try {
        userMarkerRef.current = L.marker(position, {
          icon: userIcon,
          zIndexOffset: 1000
        })
          .addTo(mapInstanceRef.current)
          .bindPopup('<b>📍 You are here</b>');

        // Center map on user location only if no radius circle is controlling the view
        if (!radiusCircleRef.current) {
          mapInstanceRef.current.setView(position, 15, { animate: true });
        }
      } catch (e) {
        console.error('Error adding user marker:', e);
      }
    };

    // Start adding marker
    if (mapLoaded && userLocation?.latitude) {
      addUserMarker();
    }

    // Cleanup
    return () => {
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [userLocation, mapLoaded]);

  // No longer using hardcoded zoom — fitBounds handles it automatically

  // Draw radius circle on map and fit bounds
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || !userLocation?.latitude) return;

    // Ensure Leaflet is available
    if (!L && window.L) {
      L = window.L;
    }
    if (!L) return;

    const map = mapInstanceRef.current;
    const radiusInMeters = searchRadius * 1000;
    const center = [userLocation.latitude, userLocation.longitude];

    // Remove old circle first
    if (radiusCircleRef.current) {
      try {
        map.removeLayer(radiusCircleRef.current);
      } catch (e) {}
      radiusCircleRef.current = null;
    }

    // Create new circle
    radiusCircleRef.current = L.circle(center, {
      radius: radiusInMeters,
      color: '#3B82F6',
      fillColor: '#3B82F6',
      fillOpacity: 0.1,
      weight: 2,
      dashArray: '8, 8'
    }).addTo(map);

    // Add popup to the circle
    radiusCircleRef.current.bindPopup(`<b>Search Radius: ${searchRadius} km</b>`);

    // Fit map so the circle fills the screen tightly but stays visible
    const circleBounds = radiusCircleRef.current.getBounds();
    const idealZoom = map.getBoundsZoom(circleBounds, false);
    map.setView(center, idealZoom + 0.8, { animate: true });

  }, [searchRadius, userLocation?.latitude, userLocation?.longitude, mapLoaded]);

  // Cleanup radius circle on unmount
  useEffect(() => {
    return () => {
      if (radiusCircleRef.current && mapInstanceRef.current) {
        try {
          mapInstanceRef.current.removeLayer(radiusCircleRef.current);
          radiusCircleRef.current = null;
        } catch (e) {}
      }
    };
  }, []);

  // Update car markers
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current) return;

    // Ensure Leaflet is available
    if (!L && window.L) {
      L = window.L;
    }
    if (!L) return;

    const map = mapInstanceRef.current;

    // Clear ALL existing markers - remove from map and from ref
    Object.keys(carMarkersRef.current).forEach(carId => {
      try {
        const marker = carMarkersRef.current[carId];
        if (marker) {
          marker.closePopup();
          marker.off();
          map.removeLayer(marker);
        }
      } catch (e) {}
    });
    carMarkersRef.current = {};

    // Also sweep any orphaned markers (safety net)
    map.eachLayer((layer) => {
      if (layer._icon && layer._icon.classList.contains('custom-car-marker')) {
        try { map.removeLayer(layer); } catch (e) {}
      }
    });

    // Add/update markers for each car
    cars.forEach(car => {
      if (!car.location?.coordinates?.latitude) return;

      const position = [car.location.coordinates.latitude, car.location.coordinates.longitude];
      const isSelected = car._id === selectedCarId;
      const distance = car.distance !== null && car.distance !== undefined ? car.distance : null;
      const icon = createCarIcon(isSelected, '#3B82F6', distance, car.vehicleType || 'car');

      // Create popup content with car details
      const distanceText = distance !== null
        ? (distance < 1 ? `${Math.round(distance * 1000)}m away` : `${distance}km away`)
        : '';

      // Get car image
      const carImage = car.images && car.images.length > 0 && car.images[0]
        ? car.images[0]
        : null;

      const popupContent = `
        <div style="min-width: 150px; max-width: 180px; font-family: system-ui, -apple-system, sans-serif;">
          ${carImage ? `
            <div style="margin: -13px -20px 6px -20px; border-radius: 8px 8px 0 0; overflow: hidden;">
              <img src="${carImage}" alt="${car.make} ${car.model}"
                style="width: 100%; height: 60px; object-fit: cover;"
                onerror="this.style.display='none'"
              />
            </div>
          ` : ''}
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px;">
            <div style="font-weight: 600; font-size: 13px; color: #1f2937;">
              ${formatName(car.make)} ${formatName(car.model)}
            </div>
            <div>
              <span style="font-size: 14px; font-weight: 700; color: #2563eb;">₹${car.pricePerDay}</span>
              <span style="font-size: 10px; color: #9ca3af;">/day</span>
            </div>
          </div>
          <div style="font-size: 11px; color: #6b7280; margin-bottom: 6px;">
            ${car.year} • ${formatName(car.fuelType)} • ${car.seats ? car.seats + ' Seats' : ''}
          </div>
          ${distanceText ? `<span style="background: #ecfdf5; color: #059669; font-size: 10px; font-weight: 500; padding: 2px 6px; border-radius: 10px; margin-right: 4px;">📍 ${distanceText}</span>` : ''}
          ${car.location?.city ? `<span style="font-size: 10px; color: #9ca3af;">📌 ${formatName(car.location.city)}</span>` : ''}
          <button data-book-car-id="${car._id}" style="width: 100%; padding: 8px; background: #16a34a; color: white; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; margin-top: 6px;">
            Book Now
          </button>
        </div>
      `;

      // Create new marker with popup
      const marker = L.marker(position, {
        icon,
        zIndexOffset: 500 // Ensure car markers are above radius circle
      })
        .addTo(mapInstanceRef.current)
        .bindPopup(popupContent, {
          maxWidth: 180,
          minWidth: 160,
          className: 'car-popup',
          closeButton: true,
          autoPan: true,
          autoPanPaddingTopLeft: L.point(20, 80),
          autoPanPaddingBottomRight: L.point(20, 20),
          keepInView: true
        });

      marker.on('click', () => {
        setSelectedCar(car);
        if (onCarSelect) onCarSelect(car);
        // Draw route line from user to car
        drawRouteLine(car);
        // Open popup
        marker.openPopup();
      });

      // Handle Book Now click inside popup
      marker.on('popupopen', () => {
        // Small delay to ensure popup DOM is ready
        setTimeout(() => {
          const bookBtn = document.querySelector(`[data-book-car-id="${car._id}"]`);
          if (bookBtn) {
            bookBtn.onclick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              marker.closePopup();
              if (onBookNow) onBookNow(car);
            };
          }
        }, 50);
      });

      carMarkersRef.current[car._id] = marker;
    });

    // Note: View/zoom is handled by the radius circle effect, don't override here
  }, [cars, selectedCarId, mapLoaded, selectedVehicleType]);

  // Center map on user location
  const handleCenterOnUser = () => {
    if (mapInstanceRef.current && userLocation?.latitude) {
      mapInstanceRef.current.setView(
        [userLocation.latitude, userLocation.longitude],
        15,
        { animate: true }
      );
    }
  };

  // Reset selection and clear route
  const handleReset = () => {
    setSelectedCar(null);
    if (onCarSelect) onCarSelect(null);

    // Remove route lines
    if (routeLineRef.current) {
      if (Array.isArray(routeLineRef.current)) {
        routeLineRef.current.forEach(line => {
          if (line && mapInstanceRef.current) mapInstanceRef.current.removeLayer(line);
        });
      } else if (mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(routeLineRef.current);
      }
      routeLineRef.current = null;
    }

    // Close all popups
    if (mapInstanceRef.current) {
      mapInstanceRef.current.closePopup();
    }

    // Center on user
    handleCenterOnUser();
  };

  // Fit all cars in view
  const handleFitAll = () => {
    if (!mapInstanceRef.current || !L) return;

    const bounds = [];
    if (userLocation?.latitude) {
      bounds.push([userLocation.latitude, userLocation.longitude]);
    }
    cars.forEach(car => {
      if (car.location?.coordinates?.latitude) {
        bounds.push([car.location.coordinates.latitude, car.location.coordinates.longitude]);
      }
    });

    if (bounds.length > 0) {
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 14, animate: true });
    }
  };

  // Close car details
  const closeCarDetails = () => {
    setSelectedCar(null);
    if (onCarSelect) onCarSelect(null);
  };

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-gray-900 rounded-xl ${className}`} style={{ height }}>
        <div className="text-center text-gray-400">
          <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-500" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Filter cars with valid coordinates — keep parent's sort order
  const sortedCars = cars.filter(car => car.location?.coordinates?.latitude);

  // Draw route line from user to car
  const drawRouteLine = (car) => {
    if (!mapInstanceRef.current || !L) return;

    // Remove all existing route lines
    if (routeLineRef.current) {
      if (Array.isArray(routeLineRef.current)) {
        routeLineRef.current.forEach(line => {
          if (line) mapInstanceRef.current.removeLayer(line);
        });
      } else {
        mapInstanceRef.current.removeLayer(routeLineRef.current);
      }
      routeLineRef.current = null;
    }

    // Draw new route if user location and car location exist
    if (userLocation?.latitude && car?.location?.coordinates?.latitude) {
      const userLatLng = [userLocation.latitude, userLocation.longitude];
      const carLatLng = [car.location.coordinates.latitude, car.location.coordinates.longitude];

      // Create main dashed line
      const mainLine = L.polyline([userLatLng, carLatLng], {
        color: '#3B82F6',
        weight: 4,
        opacity: 0.8,
        dashArray: '10, 10',
        lineCap: 'round'
      }).addTo(mapInstanceRef.current);

      // Create background line for better visibility
      const bgLine = L.polyline([userLatLng, carLatLng], {
        color: '#60A5FA',
        weight: 2,
        opacity: 0.5
      }).addTo(mapInstanceRef.current);

      // Store both lines for removal later
      routeLineRef.current = [mainLine, bgLine];

      // Fit bounds to show both user and car
      const bounds = L.latLngBounds([userLatLng, carLatLng]);
      mapInstanceRef.current.fitBounds(bounds, {
        padding: [80, 80],
        maxZoom: 15,
        animate: true
      });
    }
  };

  // Handle car click from list
  const handleCarListClick = (car) => {
    setSelectedCar(car);
    if (onCarSelect) onCarSelect(car);

    // Draw route line from user to car
    drawRouteLine(car);
  };

  return (
    <div className={`relative rounded-xl overflow-hidden ${className}`} style={{ height }}>
      {/* Main Layout: Map + Cars List */}
      <div className="flex h-full">
        {/* Map Container */}
        <div ref={mapRef} className="flex-1 h-full" />

        {/* Cars List Panel - Right Side (hidden on mobile) */}
        <div className="hidden md:flex w-72 bg-white border-l border-gray-200 flex-col overflow-hidden">
          {/* List Header */}
          <div className="p-3 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">Nearby Vehicles</h3>
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                {sortedCars.length} found
              </span>
            </div>
            {userLocation?.city && (
              <p className="text-xs text-gray-500 mt-1">📍 {userLocation.city}</p>
            )}
          </div>

          {/* Scrollable Cars List */}
          <div className="flex-1 overflow-y-auto">
            {sortedCars.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <Car className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No vehicles with location data</p>
              </div>
            ) : (
              sortedCars.map((car, index) => (
                <div
                  key={car._id}
                  onClick={() => handleCarListClick(car)}
                  className={`border-b border-gray-100 cursor-pointer hover:bg-blue-50 transition-colors ${
                    selectedCar?._id === car._id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  {/* Car Image */}
                  <div className="h-28 bg-gray-100 relative overflow-hidden">
                    {car.images && car.images.filter(img => img).length > 0 ? (
                      <img
                        src={car.images.filter(img => img)[0]}
                        alt={`${formatName(car.make)} ${formatName(car.model)}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <Car className="h-10 w-10 text-gray-300" />
                      </div>
                    )}
                    {/* Rank badge on image */}
                    <div className={`absolute top-1.5 left-1.5 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow ${
                      index === 0 ? 'bg-green-500 text-white' :
                      index === 1 ? 'bg-blue-500 text-white' :
                      index === 2 ? 'bg-yellow-500 text-white' :
                      'bg-white text-gray-600'
                    }`}>
                      {index + 1}
                    </div>
                    {/* Distance badge on image */}
                    {car.distance !== null && car.distance !== undefined && (
                      <div className="absolute top-1.5 right-1.5">
                        <span className={`inline-flex items-center text-xs font-semibold px-1.5 py-0.5 rounded-full shadow ${
                          car.distance < 2 ? 'bg-green-500 text-white' :
                          car.distance < 5 ? 'bg-blue-500 text-white' :
                          'bg-white/90 text-gray-700'
                        }`}>
                          {car.distance < 1
                            ? `${Math.round(car.distance * 1000)}m`
                            : `${car.distance}km`
                          }
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Car Info */}
                  <div className="p-2.5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0 mr-2">
                        <h4 className="font-semibold text-gray-900 text-sm truncate">
                          {formatName(car.make)} {formatName(car.model)}
                        </h4>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {car.year} &middot; {formatName(car.fuelType)} &middot; {formatName(car.transmission || 'Manual')}
                        </p>
                      </div>
                      {/* Price */}
                      <div className="text-right flex-shrink-0">
                        {car.pricePerDay && (
                          <div className="text-sm font-bold text-blue-600">
                            ₹{car.pricePerDay}
                          </div>
                        )}
                        <div className="text-xs text-gray-400">/day</div>
                      </div>
                    </div>

                    {/* Action Buttons - Show when selected */}
                    {selectedCar?._id === car._id && (
                      <div className="mt-2 flex gap-1.5">
                        {onViewDetails && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewDetails(car);
                            }}
                            className="flex-1 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium rounded-lg transition-colors"
                          >
                            Details
                          </button>
                        )}
                        {onBookNow && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onBookNow(car);
                            }}
                            className="flex-1 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors"
                          >
                            Book Now
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Loading overlay */}
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Loading map...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="absolute top-4 left-4 z-[1000]">
        <div className="bg-white rounded-xl shadow-lg p-3">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse mr-2" />
            <span className="font-medium text-gray-800">Live Map</span>
          </div>
        </div>
      </div>

      {/* Map Control Buttons */}
      {mapLoaded && (
        <div className="absolute bottom-4 left-4 z-[1000] flex flex-col gap-2">
          {/* Center on me */}
          {userLocation?.latitude && (
            <button
              onClick={handleCenterOnUser}
              className="p-3 bg-white rounded-full shadow-lg hover:bg-blue-50 transition-colors group"
              title="Center on my location"
            >
              <Navigation className="h-5 w-5 text-blue-600 group-hover:scale-110 transition-transform" />
            </button>
          )}

          {/* Fit all cars */}
          <button
            onClick={handleFitAll}
            className="p-3 bg-white rounded-full shadow-lg hover:bg-green-50 transition-colors group"
            title="Show all cars"
          >
            <svg className="h-5 w-5 text-green-600 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </button>

          {/* Reset / Clear selection */}
          {selectedCar && (
            <button
              onClick={handleReset}
              className="p-3 bg-white rounded-full shadow-lg hover:bg-red-50 transition-colors group"
              title="Clear selection"
            >
              <X className="h-5 w-5 text-red-500 group-hover:scale-110 transition-transform" />
            </button>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-12 left-2 md:bottom-4 md:left-14 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg p-1.5 md:p-2 text-[9px] md:text-xs shadow-md max-w-[250px] md:max-w-none">
        <div className="flex items-center space-x-2 md:space-x-3 flex-wrap gap-y-0.5 md:gap-y-1">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-1 ring-2 ring-blue-200" />
            <span className="text-gray-700">You</span>
          </div>
          {(() => {
            if (selectedVehicleType && selectedVehicleType !== 'all') {
              const config = getVehicleMapConfig(selectedVehicleType);
              const label = selectedVehicleType.charAt(0).toUpperCase() + selectedVehicleType.slice(1).replace('-', ' ');
              return (
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-1" style={{ backgroundColor: config.color }} />
                  <span className="text-gray-700">{config.icon === 'img' ? <><img src={config.img} className="inline w-3.5 h-3.5 mr-0.5" alt={label} />{label}</> : `${config.icon} ${label}`}</span>
                </div>
              );
            }
            // Show all vehicle types present on the map
            const vehicleTypes = [...new Set(cars.map(c => c.vehicleType || 'car'))];
            if (vehicleTypes.length === 0) {
              return (
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-600 rounded-full mr-1" />
                  <span className="text-gray-700">Vehicles</span>
                </div>
              );
            }
            return vehicleTypes.map(type => {
              const config = getVehicleMapConfig(type);
              const label = type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ');
              return (
                <div key={type} className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-1" style={{ backgroundColor: config.color }} />
                  <span className="text-gray-700">{config.icon === 'img' ? <><img src={config.img} className="inline w-3.5 h-3.5 mr-0.5" alt={label} />{label}</> : `${config.icon} ${label}`}</span>
                </div>
              );
            });
          })()}
          <div className="flex items-center">
            <div className="w-3 h-3 border-2 border-blue-500 border-dashed rounded-full mr-1" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }} />
            <span className="text-gray-700">{searchRadius}km</span>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes ripple {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(2.5); opacity: 0; }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        @keyframes userPulse {
          0% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.4); opacity: 0.4; }
          100% { transform: scale(1); opacity: 0.8; }
        }

        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }

        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }

        .user-location-marker {
          z-index: 1000 !important;
          background: none !important;
          border: none !important;
        }

        .custom-car-marker {
          background: none !important;
          border: none !important;
        }

        /* Fix Leaflet default icon styles that hide divIcon markers */
        .leaflet-div-icon {
          background: none !important;
          border: none !important;
        }

        /* Popup styling */
        .leaflet-popup {
          z-index: 10000 !important;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 12px !important;
          box-shadow: 0 10px 40px rgba(0,0,0,0.25) !important;
          overflow: visible !important;
        }
        .leaflet-popup-content {
          margin: 13px 20px !important;
        }
        .leaflet-popup-close-button {
          display: block !important;
          z-index: 10001 !important;
          width: 28px !important;
          height: 28px !important;
          font-size: 20px !important;
          line-height: 28px !important;
          text-align: center !important;
          color: #374151 !important;
          background: white !important;
          border-radius: 50% !important;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2) !important;
          top: -10px !important;
          right: -10px !important;
          padding: 0 !important;
          border: 1px solid #e5e7eb !important;
        }
        .leaflet-popup-close-button:hover {
          background: #f3f4f6 !important;
          color: #ef4444 !important;
        }
        .leaflet-control-zoom a {
          width: 36px !important;
          height: 36px !important;
          line-height: 36px !important;
          font-size: 18px !important;
          background: white !important;
          color: #374151 !important;
          border: 1px solid #e5e7eb !important;
        }
        .leaflet-control-zoom {
          border: none !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
          border-radius: 12px !important;
          overflow: hidden !important;
        }
        .leaflet-control-zoom a:hover {
          background: #f3f4f6 !important;
        }
      `}</style>
    </div>
  );
};

export default NearbyCarsMap;
